import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import {
    FormControl,
    FormGroup, ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { SearchFilterModel } from '../../models/SearchFilter-model';
import { FileService } from '../../service/FileService.service';
import { LocalStorageService } from '../../service/LocalStorageService';
import {isAvailableFileServiceFilter, keepAvailableFileServiceFilters} from '../constants/file-service-filter-fields';
import {KeyValuePipe, NgForOf} from "@angular/common";
import {MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {SharedModule} from "../shared.module";

@Component({
    selector: 'app-file-service-filters',
    templateUrl: './file-service-filters.component.html',
    standalone: true,
    imports: [
        KeyValuePipe,
        MatFormField,
        MatInput,
        MatOption,
        MatSelect,
        NgForOf,
        ReactiveFormsModule,
        SharedModule
    ],
    styleUrls: ['./file-service-filters.component.css'],
})
export class FileServiceFiltersComponent implements OnInit {
    public selectedOptions: SearchFilterModel[] = [];
    public allAvailableOptions: string[] = [];

    @Input() displayedColumns: string[] = [];
    @Output() fileServiceFilterClosed = new EventEmitter<boolean>();
    @Output() fileServiceSelectedFilters = new EventEmitter<SearchFilterModel[]>();
    @Output() selectedFiltersChange = new EventEmitter<SearchFilterModel[]>();
    @Output() filterCount = new EventEmitter<number>();

    private fileService = inject(FileService);
    private localStorageService = inject(LocalStorageService);

    searchFilterForm = new FormGroup({});

    constructor(@Inject('LOCAL_FILE_KEY') private fileServiceFilter: string) {}

    ngOnInit(): void {
        const filteredDisplayedColumns = this.displayedColumns.filter(isAvailableFileServiceFilter);
        const savedFilters = this.fileService.getSavedFilters();
        const missingSavedOptions = savedFilters.map(filter => filter.option).filter(
            option => !filteredDisplayedColumns.includes(option)
        );

        this.allAvailableOptions = [...new Set([...filteredDisplayedColumns, ...missingSavedOptions])];

        if (savedFilters.length > 0) {
            this.searchFilterForm = this.localStorageService.createFormGroupFromFilters(
                savedFilters,
                this.allAvailableOptions
            );
            Object.keys(this.searchFilterForm.controls).forEach(key => {
                const group = this.searchFilterForm.get(key) as FormGroup;
                this.setupFormGroup(group);
            });
        }
    }

    addFormControl() {
        const controlKey = `control-${Object.keys(this.searchFilterForm.controls).length + 1}`;
        const control = new FormGroup({
            option: new FormControl(this.allAvailableOptions[0]),
            value: new FormControl('', Validators.required)
        });

        this.setupFormGroup(control);
        this.searchFilterForm.addControl(controlKey, control);
    }

    private setupFormGroup(group: FormGroup) {
        const optionControl = group.get('option');
        const valueControl = group.get('value');

        if (!optionControl || !valueControl) return;

        optionControl.valueChanges.subscribe(() => {
            valueControl.setValidators(Validators.required);
            valueControl.updateValueAndValidity();
        });
    }

    clearFilters() {
        this.localStorageService.clearFilters(this.fileServiceFilter);
        Object.keys(this.searchFilterForm.controls).forEach(key => {
            this.searchFilterForm.removeControl(key);
        });
        this.filterCount.emit(0);
        this.fileService.getAllFileServiceRecords();
    }

    closeModal() {
        this.fileServiceFilterClosed.emit(false);
    }

    onSubmit() {
        this.selectedOptions = [];
        for (const group in this.searchFilterForm.controls) {
            const value = this.searchFilterForm.get(group)?.value.value?.trim();
            this.selectedOptions.push({
                option: this.searchFilterForm.get(group)?.value.option,
                value
            });
        }

        this.selectedOptions = keepAvailableFileServiceFilters(this.selectedOptions);
        this.localStorageService.saveFilters(this.fileServiceFilter, this.selectedOptions);
        this.fileServiceSelectedFilters.emit(this.selectedOptions);
        this.fileServiceFilterClosed.emit(false);
        this.filterCount.emit(this.selectedOptions.length);
    }

    deleteSearchBar(key: string) {
        this.searchFilterForm.removeControl(key);

        const updatedFilters: SearchFilterModel[] = [];
        for (const groupKey in this.searchFilterForm.controls) {
            const filterGroup = this.searchFilterForm.get(groupKey)?.value;
            updatedFilters.push({
                option: filterGroup.option,
                value: filterGroup.value,
                hitRateMin: filterGroup.hitRateMin,
                hitRateMax: filterGroup.hitRateMax
            });
        }

        const availableFilters = keepAvailableFileServiceFilters(updatedFilters);
        this.localStorageService.saveFilters(this.fileServiceFilter, availableFilters);
        this.filterCount.emit(availableFilters.length);
        this.fileService.getAllFileServiceRecords();
    }

    hasFormControls(): boolean {
        return Object.keys(this.searchFilterForm.controls).length > 0;
    }

    formatEnumKey(key: string): string {
        return key.toLowerCase().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
    }
}
